import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useWalkthrough, useWalkthroughConfig } from "./WalkthroughContext";
import { TourTooltip } from "./TourTooltip";

type Rect = { x: number; y: number; width: number; height: number };

const PADDING = 8;
const CORNER_RADIUS = 12;

function useTargetRect(target: string): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  const measure = useCallback(() => {
    const el = document.querySelector(`[data-tour="${target}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({
        x: r.x - PADDING,
        y: r.y - PADDING,
        width: r.width + PADDING * 2,
        height: r.height + PADDING * 2,
      });
    } else {
      setRect(null);
    }
  }, [target]);

  useEffect(() => {
    // Scroll target into view first
    const el = document.querySelector(`[data-tour="${target}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    // Measure after a short delay to allow scroll settling
    const t1 = setTimeout(measure, 50);
    const t2 = setTimeout(measure, 200);

    // Poll for async-loaded elements that aren't in the DOM yet
    let pollCount = 0;
    const interval = setInterval(() => {
      pollCount++;
      const found = document.querySelector(`[data-tour="${target}"]`);
      if (found) {
        found.scrollIntoView({ behavior: "smooth", block: "nearest" });
        measure();
        clearInterval(interval);
      } else if (pollCount >= 50) {
        clearInterval(interval);
      }
    }, 300);

    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearInterval(interval);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [target, measure]);

  return rect;
}

export function TourOverlay() {
  const { tourStep, nextTour, prevTour, skipTour, finishTour } = useWalkthrough();
  const { tourSteps } = useWalkthroughConfig();
  const step = tourSteps[tourStep];
  const targetRect = useTargetRect(step?.target ?? "");

  const isFirst = tourStep === 0;
  const isLast = tourStep === tourSteps.length - 1;


  if (!step) return null;

  // Position tooltip above or below based on target position
  const vh = window.innerHeight;
  const position =
    targetRect && targetRect.y + targetRect.height / 2 < vh / 2
      ? "below"
      : "above";

  return (
    <>
      {/* SVG overlay with spotlight cutout */}
      <svg
        className="fixed inset-0 z-[70] pointer-events-none"
        style={{ width: "100vw", height: "100vh" }}
      >
        <defs>
          <mask id="walkthrough-tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <motion.rect
                initial={false}
                animate={{
                  x: targetRect.x,
                  y: targetRect.y,
                  width: targetRect.width,
                  height: targetRect.height,
                }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                rx={CORNER_RADIUS}
                ry={CORNER_RADIUS}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#walkthrough-tour-mask)"
        />
      </svg>

      {/* Click blocker */}
      <div className="fixed inset-0 z-[71]" />

      {/* Tooltip — only render when target is found */}
      <AnimatePresence mode="wait">
        {targetRect && <TourTooltip
          step={step}
          stepIndex={tourStep}
          totalSteps={tourSteps.length}
          position={position}
          targetRect={targetRect}
          onNext={nextTour}
          onBack={prevTour}
          onDone={finishTour}
          onSkip={skipTour}
          isFirst={isFirst}
          isLast={isLast}
        />}
      </AnimatePresence>
    </>
  );
}
